/**
 * Shared types for persisted geolocation-agent output.
 *
 * The geolocation agent (company-mapping `Geolocation` service) maps a single
 * company to a list of physical sites. We persist that output per company as a
 * `company_geolocation` row (lifecycle: draft -> in_review -> published) plus a
 * `company_geolocation_site` child row per site, so sites can be reviewed and
 * edited individually.
 *
 * Note: this is company-scoped data following the same hub convention as
 * `company_revenues` etc. — a variant references it via `variant_inputs`, the
 * row itself carries no `variantId`. The `variant_inputs` wiring is intentionally
 * NOT part of this initial schema (see PLT-4943).
 */

export const allGeolocationStatuses = ['draft', 'in_review', 'published'] as const;
export type GeolocationStatus = (typeof allGeolocationStatuses)[number];

/**
 * Relationship of a site to the company. Mirrors company-mapping's
 * `SupplyChainPosition` (Geolocation/site_types.md). Stored as a string in the
 * DB (CHECK-constrained), not a Postgres enum.
 */
export const allSupplyChainPositions = ['Internal', 'Upstream', 'Downstream'] as const;
export type GeolocationSupplyChainPosition = (typeof allSupplyChainPositions)[number];

/**
 * How a site row was authored. Stored as a CHECK-constrained string rather than a Postgres
 * enum, mirroring `supplyChainPosition` / `locationType` on the same table.
 *
 * `manual` is the default, and what the `/site-locations` input page writes; it is also what
 * every row predating the column reads back as, so it means "not known to be agentic" rather
 * than a positive claim that a person typed it. The agentic chat flow sets `file-upload` (parsed
 * from a file the user uploaded), `chat` (typed or pasted into the conversation, or described to
 * the agent there) or `identification-service` (discovered by `identify_company_sites`).
 *
 * This records where a row came from, not whether a human has since checked it: editing an
 * agent-proposed row in the card leaves the row's source unchanged. A machine pass that wanted
 * to preserve human corrections would need that second signal, which this column does not carry.
 */
// Every value must also be in the `company_geolocation_site_source_check` constraint (see the
// migrations that add and extend it); a value missing there is a failed save, not a validation error.
export const allGeolocationSiteSources = ['manual', 'file-upload', 'chat', 'identification-service'] as const;
export type GeolocationSiteSource = (typeof allGeolocationSiteSources)[number];

export const DEFAULT_GEOLOCATION_SITE_SOURCE: GeolocationSiteSource = 'manual';

/**
 * How well one geocoded coordinate represents the place it was resolved from; see
 * `LocationExtentPrecision` in `api-types.ts`, which documents each value. Here rather than there
 * because the site shape below stores it and `api-types.ts` imports from this module.
 */
// Every value must also be in the `company_geolocation_site_geocode_precision_check` constraint.
export const allLocationExtentPrecisions = ['point', 'coarse', 'area', 'unknown'] as const;

/**
 * Canonical site-type values, mirroring the taxonomy the site-identification service
 * produces (company-mapping `Geolocation/site_types.md`, parsed there into `SITE_TYPES`).
 * `locationType` is stored as a free string in the DB rather than a DB-side enum (the
 * taxonomy is owned by company-mapping and may evolve), so this list is the UI's
 * dropdown source, not a hard constraint — keep it in sync with `site_types.md`.
 */
export const allGeolocationSiteTypes = [
  'Global HQ',
  'HQ',
  'Corporate/administrative',
  'Production',
  'Extraction',
  'Biological',
  'Waste & recovery sites',
  'Logistics/infrastructure',
  'Utility infrastructure',
  'Construction sites',
  'Data centers',
  'Retail/customer-facing',
] as const;
export type GeolocationSiteType = (typeof allGeolocationSiteTypes)[number];

/** Public shape of a single persisted site. */
export interface CompanyGeolocationSite {
  id: number;
  companyGeolocationId: number;
  /** Schema.org-style postal address fields (all nullable — the agent may resolve only some). */
  streetAddress: string | null;
  addressLocality: string | null;
  addressRegion: string | null;
  postalCode: string | null;
  addressCountry: string | null;
  /**
   * Site-type taxonomy value (e.g. "Headquarters", "Manufacturing"). The canonical
   * taxonomy is owned by company-mapping (`Geolocation/site_types.md`) and may evolve,
   * so this is stored as a free string rather than a DB-side enum.
   */
  locationType: string;
  supplyChainPosition: GeolocationSupplyChainPosition;
  /**
   * How this row was authored — see `allGeolocationSiteSources`. Rows written before the
   * column existed read back as `manual`.
   */
  source: GeolocationSiteSource;
  /**
   * User-facing location label (e.g. "Gigafactory Texas"), distinct from the longer
   * free-text `description`. Nullable — the site-identification agent does not produce a
   * name, so it is null for auto-identified sites until a user fills it in.
   */
  name: string | null;
  description: string | null;
  /** URL where the agent saw this site listed. */
  evidenceUrl: string | null;
  latitude: number | null;
  longitude: number | null;
  /**
   * What the geocoder said about these coordinates when Upright looked the address up (PLT-7875).
   * Null for coordinates it did not look up: typed in, given by the agent, or saved before the
   * columns existed. `siteCoordinateCheck` reads them.
   */
  geocodePrecision: (typeof allLocationExtentPrecisions)[number] | null;
  geocodePartialMatch: boolean | null;
  /**
   * This site's percentage share of its own `supplyChainPosition` — not of the
   * company (PLT-6333). Each position runs to 100 independently, so Internal,
   * Upstream and Downstream can each carry a full 100.
   *
   * A position summing below 100 is partial coverage and is scored as such; one
   * summing above 100 is refused at publish. `null` means "no explicit weight",
   * and a position with no weights splits 100 equally across its sites.
   * `resolveEffectiveSiteWeights` owns the resolution.
   */
  weight: number | null;
  createdAt: string;
  updatedAt: string;
}

/** A site without its row's bookkeeping: what a chat approval records of each site it approves (PLT-7453). */
export type CompanyGeolocationSiteValues = Omit<
  CompanyGeolocationSite,
  'companyGeolocationId' | 'createdAt' | 'updatedAt'
>;

/**
 * Lightweight summary of a published `company_geolocation` row, used to populate the
 * "Site location set" selector on the internal variant-edit view (PLT-5329). Only
 * published rows are linkable to a variant, so this summary is produced only for them.
 */
export interface PublishedGeolocationSummary {
  id: number;
  publishedAt: string | null;
  createdAt: string;
  /** Email of the user who created the geolocation, or null if the creator can't be resolved. */
  createdByEmail: string | null;
  siteCount: number;
}

/** Public shape of a persisted geolocation result for a company. */
export interface CompanyGeolocation {
  id: number;
  companyId: string;
  status: GeolocationStatus;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  publishedAt: string | null;
  publishedBy: number | null;
  sites?: CompanyGeolocationSite[];
}
